import { useState, useEffect, useMemo } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import {
  Wine, Coffee, Beer, GlassWater, Citrus, Sparkles, Search, X, Check, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const CATEGORY_ICONS = {
  "Refrescos": Sparkles,
  "Agua": GlassWater,
  "Zumos": Citrus,
  "Cervezas": Beer,
  "Vinos": Wine,
  "Copas y Combinados": Wine,
  "Cafes e Infusiones": Coffee,
};

const CATEGORY_COLORS = {
  "Refrescos": "text-red-500 bg-red-500/10",
  "Agua": "text-sky-500 bg-sky-500/10",
  "Zumos": "text-amber-500 bg-amber-500/10",
  "Cervezas": "text-yellow-600 bg-yellow-600/10",
  "Vinos": "text-purple-500 bg-purple-500/10",
  "Copas y Combinados": "text-indigo-500 bg-indigo-500/10",
  "Cafes e Infusiones": "text-orange-700 bg-orange-700/10",
};

export default function BeverageSelector({ open, onClose, menuId, restaurantId, onAdded }) {
  const [beverages, setBeverages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/universal/beverages`)
      .then(r => r.json())
      .then(data => {
        setBeverages(data.beverages || []);
        const commonIds = (data.beverages || []).filter(b => b.is_common).map(b => b.bev_id);
        setSelected(new Set(commonIds));
      })
      .catch(() => toast.error("Error al cargar bebidas"))
      .finally(() => setLoading(false));
  }, [open]);

  const categories = useMemo(() => {
    const cats = new Set();
    beverages.forEach(b => cats.add(b.category));
    return Array.from(cats);
  }, [beverages]);

  const filtered = useMemo(() => {
    let list = beverages;
    if (activeCategory !== "all") list = list.filter(b => b.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(b => b.name.toLowerCase().includes(q) || b.brand.toLowerCase().includes(q));
    }
    return list;
  }, [beverages, activeCategory, search]);

  const toggle = (bevId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(bevId)) next.delete(bevId);
      else next.add(bevId);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(beverages.map(b => b.bev_id)));
  const selectNone = () => setSelected(new Set());
  const selectCommon = () => setSelected(new Set(beverages.filter(b => b.is_common).map(b => b.bev_id)));

  const handleSubmit = async () => {
    if (selected.size === 0) { toast.error("Selecciona al menos una bebida"); return; }
    setSubmitting(true);
    try {
      const resp = await api.request(`/restaurants/${restaurantId}/menus/${menuId}/add-beverages`, {
        method: "POST",
        body: JSON.stringify({ bev_ids: Array.from(selected) }),
      });
      toast.success(`${resp.beverages_added} bebidas anadidas al menu`);
      onAdded?.();
      onClose();
    } catch (err) {
      toast.error("Error al anadir bebidas");
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="beverage-selector-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-bold">Anadir Bebidas al Menu</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{selected.size} de {beverages.length} seleccionadas</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors"><X className="w-5 h-5" /></button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-border space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar bebida o marca..."
              className="pl-9" data-testid="beverage-search"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${activeCategory === "all" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
            >Todas</button>
            {categories.map(cat => {
              const Icon = CATEGORY_ICONS[cat] || Sparkles;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${activeCategory === cat ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                >
                  <Icon className="w-3 h-3" />{cat}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <button onClick={selectCommon} className="text-[11px] text-primary hover:underline" data-testid="beverage-select-common">Seleccionar comunes</button>
            <span className="text-muted-foreground/40">|</span>
            <button onClick={selectAll} className="text-[11px] text-muted-foreground hover:underline">Todas</button>
            <span className="text-muted-foreground/40">|</span>
            <button onClick={selectNone} className="text-[11px] text-muted-foreground hover:underline">Ninguna</button>
          </div>
        </div>

        {/* List */}
        <ScrollArea className="flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground text-sm">No se encontraron bebidas</div>
          ) : (
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filtered.map(bev => {
                const isSelected = selected.has(bev.bev_id);
                const catColor = CATEGORY_COLORS[bev.category] || "text-muted-foreground bg-muted";
                return (
                  <button
                    key={bev.bev_id}
                    onClick={() => toggle(bev.bev_id)}
                    data-testid={`beverage-item-${bev.bev_id}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                      isSelected ? "border-primary/50 bg-primary/5" : "border-border hover:border-border/80 hover:bg-muted/30"
                    }`}
                  >
                    {/* Image */}
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                      {bev.image_url ? (
                        <img src={bev.image_url} alt={bev.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <GlassWater className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium truncate">{bev.name}</span>
                        {bev.is_alcoholic && <Badge variant="outline" className="text-[8px] px-1 py-0 border-amber-500/30 text-amber-500">Alc.</Badge>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${catColor}`}>{bev.category}</span>
                        <span className="text-[10px] text-muted-foreground">{bev.volume}</span>
                      </div>
                    </div>
                    {/* Price */}
                    <span className="text-sm font-semibold text-primary flex-shrink-0">{(bev.default_price || 0).toFixed(2)}&euro;</span>
                    {/* Check */}
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {selected.size} bebida{selected.size !== 1 ? "s" : ""} seleccionada{selected.size !== 1 ? "s" : ""}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="beverage-cancel-btn">Cancelar</Button>
            <Button onClick={handleSubmit} disabled={submitting || selected.size === 0} data-testid="beverage-add-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wine className="w-4 h-4 mr-2" />}
              Anadir {selected.size} bebidas
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
